import { type MouseEvent, type ReactElement, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { r, s, typographyTokens } from '@/theme/tokens';
import { Box, Chip, Link, Text, type Theme, Tooltip, type TooltipProps } from '@/ui';

export const CLONE_META_DETAIL_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: s('sm'),
  pl: s('xxs'),
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
} as const;

export const CLONE_META_DETAIL_VALUE_SX = {
  display: 'block',
  minWidth: 0,
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

const CLONE_META_DETAIL_TEXT_SX = {
  '&&': {
    fontSize: '13px',
    fontWeight: typographyTokens.captionSmall.fontWeight,
    lineHeight: 1.05,
  },
} as const;

const CLONE_META_DETAILS_LINK_SX = {
  display: 'inline-block',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: '13px !important',
  fontWeight: `${typographyTokens.captionSmall.fontWeight} !important`,
  lineHeight: '1.05 !important',
} as const;

const CLONE_META_VALUE_WRAPPER_SX = {
  display: 'inline-block',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

export const CLONE_TOOLTIP_SLOT_PROPS: TooltipProps['slotProps'] = {
  tooltip: {
    sx: {
      fontSize: '15px',
      lineHeight: 1.4,
    },
  },
} as const;

type TableMetaRowProps = {
  title: string;
  icon: ReactElement;
  value: ReactNode;
  valueTitle?: string | null;
  href?: string | null;
  disableLinks?: boolean;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  textSx?: object;
  linkSx?: object;
};

const renderValueContent = ({
  value,
  href,
  disableLinks,
  onClick,
  linkSx,
}: Pick<
  TableMetaRowProps,
  'value' | 'href' | 'disableLinks' | 'onClick' | 'linkSx'
>): ReactElement => {
  if (disableLinks || href === null || href === undefined) {
    return (
      <Box component="span" sx={CLONE_META_VALUE_WRAPPER_SX}>
        {value}
      </Box>
    );
  }

  return (
    <Link
      component={RouterLink}
      to={href}
      underline="hover"
      color="inherit"
      onClick={onClick}
      sx={linkSx ?? CLONE_META_DETAILS_LINK_SX}
    >
      {value}
    </Link>
  );
};

type TableMetaInlineRowProps = {
  icon: ReactElement;
  content: ReactNode;
  textSx?: object;
};

export const TableMetaInlineRow = ({
  icon,
  content,
  textSx,
}: TableMetaInlineRowProps): ReactElement => {
  return (
    <Text
      role="captionSmall"
      color="textSecondary"
      dataGridTypography="custom"
      sx={textSx ?? CLONE_META_DETAIL_TEXT_SX}
    >
      <Box component="span" sx={CLONE_META_DETAIL_ROW_SX}>
        {icon}
        <Box component="span" sx={CLONE_META_DETAIL_VALUE_SX}>
          {content}
        </Box>
      </Box>
    </Text>
  );
};

export const TableMetaRow = ({
  title,
  icon,
  value,
  valueTitle,
  href,
  disableLinks = false,
  onClick,
  textSx,
  linkSx,
}: TableMetaRowProps): ReactElement => {
  const content = renderValueContent({ value, href, disableLinks, onClick, linkSx });

  return (
    <Text
      role="captionSmall"
      color="textSecondary"
      dataGridTypography="custom"
      sx={textSx ?? CLONE_META_DETAIL_TEXT_SX}
    >
      <Box component="span" sx={CLONE_META_DETAIL_ROW_SX}>
        <Tooltip title={title} arrow slotProps={CLONE_TOOLTIP_SLOT_PROPS}>
          {icon}
        </Tooltip>
        <Box component="span" sx={CLONE_META_DETAIL_VALUE_SX}>
          <Tooltip
            title={valueTitle ?? ''}
            arrow
            slotProps={CLONE_TOOLTIP_SLOT_PROPS}
            disableHoverListener={!valueTitle}
          >
            <Box component="span" sx={[CLONE_META_VALUE_WRAPPER_SX, { width: 'fit-content' }]}>
              {content}
            </Box>
          </Tooltip>
        </Box>
      </Box>
    </Text>
  );
};

type TableMetaStackProps = {
  children: ReactNode;
  rowGap?: string;
};

export const TableMetaStack = ({
  children,
  rowGap = s('md'),
}: TableMetaStackProps): ReactElement => {
  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        rowGap,
      }}
    >
      {children}
    </Box>
  );
};

type TableStatusMetaContentProps = {
  statusLabel: string;
  statusHref?: string | null;
  onStatusClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  chipTone: React.ComponentProps<typeof Chip>['tone'];
  chipIcon: ReactElement;
  chipBackgroundColor: (theme: Theme) => string;
  chipForegroundColor: (theme: Theme) => string;
  durationLabel: string;
  durationIcon: ReactElement;
  durationTitle?: string;
  timestampLabel: string;
  timestampTitle?: string | null;
  timestampIcon: ReactElement;
  timestampRowTitle?: string;
  compactTopSpacing?: boolean;
};

export const TableStatusMetaContent = ({
  statusLabel,
  statusHref = null,
  onStatusClick,
  chipTone,
  chipIcon,
  chipBackgroundColor,
  chipForegroundColor,
  durationLabel,
  durationIcon,
  durationTitle = 'Duration',
  timestampLabel,
  timestampTitle,
  timestampIcon,
  timestampRowTitle = 'Executed',
  compactTopSpacing = false,
}: TableStatusMetaContentProps): ReactElement => (
  <TableMetaStack rowGap={s('xs')}>
    <Chip
      size="xs"
      icon={chipIcon}
      label={
        statusHref ? (
          <Link
            component={RouterLink}
            to={statusHref}
            underline="hover"
            color="inherit"
            onClick={onStatusClick}
          >
            {statusLabel}
          </Link>
        ) : (
          statusLabel
        )
      }
      tone={chipTone}
      sx={{
        mt: s('xs'),
        mb: compactTopSpacing ? s('xs') : s('sm'),
        width: 'fit-content',
        maxWidth: '100%',
        alignSelf: 'flex-start',
        borderRadius: r('pill'),
        backgroundColor: chipBackgroundColor,
        color: chipForegroundColor,
        '& .MuiChip-label': {
          color: 'inherit',
          fontWeight: 500,
          fontSize: '15px !important',
        },
        '& .MuiChip-label .MuiTypography-root': {
          color: 'inherit',
          fontWeight: 'inherit',
          fontSize: '15px !important',
        },
        '& .MuiChip-label a': {
          color: 'inherit',
          fontWeight: 'inherit',
          fontSize: '15px !important',
        },
        '& .MuiChip-label *': {
          fontSize: '15px !important',
        },
        '& .MuiChip-icon': {
          color: 'inherit',
          fontSize: (theme) => theme.app.iconSizes.sm,
          marginRight: '-6px',
        },
      }}
    />
    <TableMetaRow title={durationTitle} icon={durationIcon} value={durationLabel} />
    <TableMetaRow
      title={timestampRowTitle}
      icon={timestampIcon}
      value={timestampLabel}
      valueTitle={timestampTitle}
    />
  </TableMetaStack>
);

export { CLONE_META_DETAIL_TEXT_SX, CLONE_META_DETAILS_LINK_SX };
